import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted } from 'vue';

import { api } from '@api';
import { ls } from '@services/ls';

import Icon, { type IconDefinition } from '@components/Icon.vue';

import { faExclamationTriangle } from '@icons/solid/faExclamationTriangle';
import { faMegaphone } from '@icons/solid/faMegaphone';

export interface FixedPositionInfo {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

interface AlertInfo {
  id: string;
  text: string;
  link: string | null;
}

interface BannerInfo {
  type: 'status' | 'announcement';
  id: string;
  text: string;
  link: string | null;
  icon: IconDefinition;
}


const DISMISSED_STATUS_LS_KEY = 'zephyr.dismissedStatus';
const DISMISSED_ANNOUNCEMENT_LS_KEY = 'zephyr.dismissedAnnouncement';

const __default__ = { name: 'StatusAlertBanner' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const loadingComplete = ref(false);

const statusData = ref<AlertInfo | null>(null);
const announcementData = ref<AlertInfo | null>(null);

const banners = computed(() => {
  const items: BannerInfo[] = [];

  if (statusData.value) {
    items.push({
      type: 'status',
      id: statusData.value.id,
      text: statusData.value.text,
      link: statusData.value.link,
      icon: faExclamationTriangle,
    });
  }

  if (announcementData.value) {
    items.push({
      type: 'announcement',
      id: announcementData.value.id,
      text: announcementData.value.text,
      link: announcementData.value.link,
      icon: faMegaphone,
    });
  }

  return items;
});

const showBanners = computed(() => {
  return !!loadingComplete.value && banners.value.length > 0;
});

onMounted(() => {
  void getAlertData();
});

async function getStatusData() {
  const statusItems = await api.siteStatus.getStatus();

  if (statusItems.length === 0) {
    return;
  }

  // Get the most recent status item.
  const status = statusItems.sort((a, b) => {
    return new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime();
  })[0] as api.siteStatus.SiteStatusItem;

  const date = new Date(status.pubDate);

  // Don't show if the status is older than 24 hours.
  if (Date.now() - date.getTime() > 24 * 60 * 60 * 1000) {
    return;
  }

  // console.log(
  //   'time (in hours) since latest status update: ',
  //   (Date.now() - date.getTime()) / (1000 * 60 * 60),
  // );

  const id = status.guid.replace(status.link, '');

  // Don't show if dismissed
  if (id === getDismissedAlert(DISMISSED_STATUS_LS_KEY)) {
    return;
  }

  const text = status.description;

  let link: string | null;

  // Don't include the link if it doesn't link to a specific  status/incident
  // page.
  if (status.link === 'https://status.zephyr-sim.com/') {
    link = null;
  } else {
    link = status.link;
  }

  statusData.value = {
    id,
    text,
    link,
  };
}

async function getAnnouncementData() {
  const data = await api.siteStatus.getAnnouncement();

  if (data.hidden || !data.digest || !data.link || !data.html) {
    return;
  }

  // Don't show if dismissed
  if (data.digest === getDismissedAlert(DISMISSED_ANNOUNCEMENT_LS_KEY)) {
    return;
  }

  const tempNode = document.createElement('div');
  tempNode.innerHTML = data.html;

  const announcementText = tempNode.textContent?.trim() ?? '';
  tempNode.remove();

  announcementData.value = {
    id: data.digest,
    text: announcementText,
    link: data.link,
  };
}

async function getAlertData() {
  await getAnnouncementData();
  await getStatusData();

  loadingComplete.value = true;

  // console.log({
  //   statusData: statusData.value,
  //   announcementData: announcementData.value,
  // });
}

//#region Helper Functions

function getDismissedAlert(key: string) {
  return ls.get(key) ?? null;
}

function openLink(link: string) {
  window.open(link, '_blank');
}

function dismissBanner(id: string) {
  if (id === statusData.value?.id) {
    ls.set(DISMISSED_STATUS_LS_KEY, id);

    statusData.value = null;
  } else if (id === announcementData.value?.id) {
    ls.set(DISMISSED_ANNOUNCEMENT_LS_KEY, id);

    announcementData.value = null;
  }
}

//#endregion Helper Functions

return { __sfc: true,DISMISSED_STATUS_LS_KEY, DISMISSED_ANNOUNCEMENT_LS_KEY, loadingComplete, statusData, announcementData, banners, showBanners, getStatusData, getAnnouncementData, getAlertData, getDismissedAlert, openLink, dismissBanner, Icon }
}

})